import React, {useCallback, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { stopReportActions } from '../store';
import {DataGrid, GridOverlay} from '@material-ui/data-grid';
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { Paper } from '@material-ui/core';
import {formatDistance, formatHours, formatDate, formatVolume, formatPosition} from '../common/formatter';
import ReportFilter from './ReportFilter';
import ReportLayoutPage from './ReportLayoutPage';
import { useAttributePreference } from '../common/preferences';
import { t } from '../common/LocalizationProvider';
import AddressCell from '../details/AddressCell';
import {cyberStyles} from "../cssShared/cyberTheme";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

const useStyles = makeStyles((theme) => ({
  ...cyberStyles(theme),
}));

const Filter = ({ loading, setLoading }) => {
  const dispatch = useDispatch();
  const [openSnackbar, setOpenSnackbar] = useState(null)

  const handleSubmit = useCallback( async (deviceIds, from, to, mail, headers) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let query = new URLSearchParams({ from, to, mail });
    deviceIds.forEach(deviceId => query.append("deviceId", deviceId))
    if (headers.Accept !== 'application/json')
      query.append("timeZone", timeZone)

    const response = await fetch(`/api/reports/stops?${query.toString()}`, { headers });
    if (response.ok) {
      if (mail)
        setOpenSnackbar('EmailReportAlert')
      const contentType = response.headers.get('content-type');
      if (contentType) {
        if (contentType === 'application/json') {
          const res = await response.json()
          dispatch(stopReportActions.setItems(res));
          if (res.length === 0)
            setOpenSnackbar('replayNoData')
        } else {
          window.location.assign(window.URL.createObjectURL(await response.blob()));
          setOpenSnackbar('ExcelFileExported')
        }
      }
    }
    else
      setOpenSnackbar('UnknownProblem')
    setLoading(null)
  },[dispatch])

  return <ReportFilter handleSubmit={handleSubmit} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} loading={loading} setLoading={setLoading}/>;
};

const StopReportPage = () => {
  const classes = useStyles();
  const theme = useTheme();

  const distanceUnit = useAttributePreference('distanceUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const items = useSelector(state => state.stopReport.items);
  const [loading, setLoading] = useState(null);

  const columns = [
    {
      headerName: t('reportDeviceName'),
      field: 'deviceName',
      type: 'string',
      width: 220,
      // valueFormatter: ({ value }) => formatPosition(value),
      renderCell: (params) => {
        return (
            <Tooltip title={formatPosition(params.value)} placement="top-start" arrow>
              <div>
                {formatPosition(params.value)}
              </div>
            </Tooltip>
        );
      },
    }, {
      headerName: t('reportStartTime'),
      field: 'startTime',
      type: 'dateTime',
      width: theme.dimensions.columnWidthDate,
      valueFormatter: ({ value }) => formatDate(value),
    }, {
      headerName: t('positionOdometer'),
      field: 'startOdometer',
      type: 'number',
      width: theme.dimensions.columnWidthNumber,
      valueFormatter: ({ value }) => formatDistance(value, distanceUnit),
    }, {
      headerName: t('positionAddress'),
      field: 'address',
      type: 'string',
      // hide: true,
      width: theme.dimensions.columnWidthString,
      renderCell: (params) => <AddressCell latitude={params.row?.latitude} longitude={ params.row?.longitude}/>,
    }, {
      headerName: t('reportEndTime'),
      field: 'endTime',
      type: 'dateTime',
      width: theme.dimensions.columnWidthDate,
      valueFormatter: ({ value }) => formatDate(value),
    }, {
      headerName: t('reportDuration'),
      field: 'duration',
      type: 'string',
      width: theme.dimensions.columnWidthString,
      valueFormatter: ({ value }) => formatHours(value),
    },{
      headerName: t('crossings'),
      field: 'crossings',
      type: 'string',
      width: theme.dimensions.columnWidthString,
      valueFormatter: ({ value }) => value,
    },
    //   {
    //   headerName: t('reportEngineHours'),
    //   field: 'engineHours',
    //   type: 'string',
    //   width: theme.dimensions.columnWidthString,
    //   valueFormatter: ({ value }) => formatHours(value),
    // },
    {
      headerName: t('reportSpentFuel'),
      field: 'spentFuel',
      type: 'number',
      width: theme.dimensions.columnWidthNumber,
      hide: true,
      valueFormatter: ({ value }) => formatVolume(value, volumeUnit),
    }]

  return (
      <ReportLayoutPage filter={<Filter loading={loading} setLoading={setLoading} />}>
        <Paper className={classes.paper}>
          <DataGrid
              loading={loading}
              rows={items}
              columns={columns}
              // hideFooterPagination
              autoHeight
              getRowId={(row) => items.indexOf(row)}
              disableSelectionOnClick
              components={{
                LoadingOverlay: () => (
                    <GridOverlay>
                      <CircularProgress className={classes.color} />
                    </GridOverlay>
                )
              }}
          />
        </Paper>
      </ReportLayoutPage>
  );
};

export default StopReportPage;